import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// In-memory session cache so we don't re-hit the backend for the same
// (lang, text) tuple during a single page session. The BACKEND already
// caches in Mongo permanently — this is purely a UX latency cut.
const SESSION_CACHE = new Map(); // key: `${lang}::${text}` → translated

const langKey = (lang, text) => `${lang}::${text}`;

/**
 * useTranslatedTexts — translates an array of English strings into the
 * customer's currently selected language via Google Cloud Translation
 * (backend cached permanently in Mongo).
 *
 *  - Returns identity (original list) immediately on first render so the
 *    UI never blocks waiting for the network.
 *  - Replaces with translations as soon as the backend responds.
 *  - When the active language is English (or empty input) the call is
 *    skipped entirely — zero backend hits.
 *
 *  @param {string[]} texts            Source English strings.
 *  @param {object} options            Optional options.
 *  @param {boolean} options.disabled  When true, never call the backend.
 */
export function useTranslatedTexts(texts, options = {}) {
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0];

  // Stable key for the useEffect dependency.
  const fingerprint = useMemo(
    () => (Array.isArray(texts) ? texts.join('§§§') : ''),
    [texts],
  );

  const [out, setOut] = useState(() => texts || []);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (options.disabled) { setOut(texts || []); return; }
    if (!texts || texts.length === 0) { setOut([]); return; }

    // English → identity, no API call.
    if (lang === 'en') { setOut(texts); return; }

    // Resolve via session cache first, only ship the misses to the backend.
    const cached = texts.map((t) => SESSION_CACHE.get(langKey(lang, t)));
    const missingIdx = [];
    const missingTexts = [];
    cached.forEach((v, i) => {
      if (v == null) {
        missingIdx.push(i);
        missingTexts.push(texts[i]);
      }
    });

    if (missingIdx.length === 0) {
      setOut(cached);
      return;
    }

    setLoading(true);
    let cancelled = false;
    const targetLang = mapLang(lang);

    axios
      .post(`${API}/i18n/translate`, {
        texts: missingTexts,
        target_lang: targetLang,
        source_lang: 'en',
      })
      .then((res) => {
        if (cancelled) return;
        const fresh = res.data?.translations || [];
        const merged = [...cached];
        missingIdx.forEach((i, k) => {
          const v = fresh[k] || texts[i];
          merged[i] = v;
          SESSION_CACHE.set(langKey(lang, texts[i]), v);
        });
        setOut(merged);
      })
      .catch(() => {
        // Backend already falls back to source; we just keep originals.
        if (cancelled) return;
        setOut(texts);
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
    // fingerprint covers content changes; lang covers selector switches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fingerprint, lang, options.disabled]);

  return { translated: out, loading };
}

// Map our short i18n codes to Google Translate's expected target codes.
// (Most are identical; zh is the one that needs explicit `zh-CN`.)
function mapLang(code) {
  if (code === 'zh') return 'zh-CN';
  return code;
}
